'use client';
import {
  BusinessShoppingCart,
  BusinessShoppingCartSolid,
  NavigationMenu,
  NavigationMinus,
} from '@energy-retailer/icons';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';
import { Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Button } from '@/components/button';
import { Cart } from '@/components/cart';
import { Icon } from '@/components/icon';
import { Text } from '@/components/text';
import { MobileNavigation } from '@/components/navigation/mobile-menu';
import { ABOUT_US_URL, COMPARE_PLANS_URL, HELP_CENTER_URL } from '@/constants';
import { useStore } from '@/lib/use-store';

import { useEnvContext } from '../../providers';
import { CustomLink } from '../custom-link';

import { HeaderDropdown } from './header-dropdown';

import type {
  HeaderDropdown as HeaderDropdownType,
  Page,
} from '@/generated/graphql';
import type { ReactNode } from 'react';

const MOBILE_NAVIGATION_ANIMATION_LENGTH = 300;

export const Header = ({
  children,
  items,
}: {
  children: ReactNode;
  items: HeaderDropdownType[];
}) => {
  const pathname = usePathname();
  const [showHeader, setShowHeader] = useState(true);
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isShowShadow, setIsShowShadow] = useState(false);

  const { LOGIN_URL } = useEnvContext();

  const cartStore = useStore(useJoinFormStore, (state) => state);
  const isSignUp =
    pathname.includes('sign-up') && !pathname.includes('sign-up/confirmation');
  const headerPosition = isSignUp ? '' : 'fixed';

  // Toggle isClosing when animation starts/ends
  const toggleMobileNavigation = () => {
    setIsClosing(true);
    setIsMobileNavigationOpen(!isMobileNavigationOpen);

    setTimeout(() => {
      setIsClosing(false);
    }, MOBILE_NAVIGATION_ANIMATION_LENGTH);
  };

  // Close mobile nav on route change
  useEffect(() => {
    setIsMobileNavigationOpen(false);
  }, [pathname]);

  // Show header on scroll up
  useEffect(() => {
    let prevScrollPos;

    const handleScroll = () => {
      if (isSignUp) {
        setShowHeader(true);
      } else {
        const currentScrollPos = window.scrollY;
        const isScrollingDown =
          currentScrollPos > 70 && prevScrollPos < currentScrollPos;
        const isScrollingUp = prevScrollPos > currentScrollPos;

        setIsShowShadow(isScrollingUp && currentScrollPos > 70);
        setShowHeader(!isScrollingDown);
        prevScrollPos = currentScrollPos <= 0 ? 0 : currentScrollPos;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSignUp]);

  // Prevent scrolling when menu is open
  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isMobileNavigationOpen);
    document.body.classList.toggle('xl:overflow-auto', isMobileNavigationOpen);

    return () => {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('xl:overflow-auto');
    };
  }, [isMobileNavigationOpen]);

  return (
    <>
      <Transition
        as="header"
        show={showHeader}
        enter="transition-transform duration-200 ease-out"
        enterFrom="transform translate-y-[-100%]"
        enterTo="transform translate-y-0"
        leave="transition-transform duration-200 ease-in"
        leaveFrom="transform translate-y-0"
        leaveTo="transform translate-y-[-100%]"
        className={clsx([
          'inset-x-0 top-0 z-30 min-h-[4rem] border-b border-gray-100 bg-white',
          headerPosition,
          isShowShadow && !isMobileNavigationOpen ? 'shadow' : 'shadow-none',
        ])}
      >
        <div className="xl:container max-[1370px]:px-0">
          <div className="flex justify-between px-d py-c sm:px-c sm:py-b min-[1370px]:px-0">
            <div className="grid shrink-0 grid-flow-col items-center gap-a">
              <button
                aria-label="Menu"
                type="button"
                className="mx-[2px] xl:hidden"
                onClick={toggleMobileNavigation}
              >
                <Icon
                  icon={
                    isMobileNavigationOpen ? (
                      <NavigationMinus />
                    ) : (
                      <NavigationMenu />
                    )
                  }
                />
              </button>
              <CustomLink
                href="/"
                onClick={() => {
                  setIsMobileNavigationOpen(false);
                }}
                confirmationRequired
              >
                <Image
                  src="/powershop-logo.png"
                  alt="Powershop logo"
                  className="h-[1.5rem] w-auto min-[1370px]:h-[2.5rem]"
                  width={177}
                  height={40}
                />
              </CustomLink>

              <nav
                className="-mb-2 hidden xl:block"
                data-testid="desktop-menu"
              >
                <ul className="flex gap-a">
                  {items.map((dropdown: HeaderDropdownType) => (
                    <HeaderDropdown
                      key={dropdown.title}
                      title={dropdown.title || ''}
                      link={dropdown?.link ?? null}
                      quickLinks={
                        dropdown.quickLinksCollection?.items as Page[]
                      }
                    />
                  ))}
                </ul>
              </nav>
            </div>
            <div className="grid grid-flow-col items-center gap-[12px] min-[1370px]:gap-b">
              <Button
                variant="navigation"
                size="sm"
                href={ABOUT_US_URL}
                className="text-[16px] !font-medium max-xl:hidden"
              >
                About
              </Button>
              <Button
                variant="navigation"
                size="sm"
                href={HELP_CENTER_URL}
                className="text-[16px] !font-medium max-xl:hidden"
              >
                Help
              </Button>
              {!isSignUp && (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    href={COMPARE_PLANS_URL}
                    onClick={() => {
                      setIsMobileNavigationOpen(false);
                    }}
                  >
                    See Plans
                  </Button>
                  <Button
                    href={LOGIN_URL}
                    size="sm"
                    variant="secondary"
                    className="hidden xl:flex"
                  >
                    Login
                  </Button>
                </>
              )}
              <Button
                ariaLabel="Cart"
                icon={
                  cartStore?.electricityPlanId || cartStore?.gasPlanId ? (
                    <div className="relative flex w-6 flex-col gap-1">
                      <div className="absolute right-0 top-0 h-2 w-2 rounded-full bg-pink-500" />
                      <BusinessShoppingCartSolid className="h-6 w-6" />
                      <Text className="!mt-auto text-[14px] !leading-none max-sm:hidden">
                        Cart
                      </Text>
                    </div>
                  ) : (
                    <div className="relative flex w-6 flex-col">
                      <BusinessShoppingCart className="h-6 w-6" />
                      <Text className="!mt-auto text-[14px] !leading-none max-sm:hidden">
                        Cart
                      </Text>
                    </div>
                  )
                }
                variant="navigation"
                // Overrides pink hover from navigation class
                className="!py-0 hover:text-[inherit] max-sm:!px-0"
                size="sm"
                onClick={() => {
                  setIsCartDrawerOpen(!isCartDrawerOpen);
                }}
              />
            </div>
          </div>
        </div>
      </Transition>

      <Transition
        show={isMobileNavigationOpen}
        enter={`transition-transform ease-out duration-${MOBILE_NAVIGATION_ANIMATION_LENGTH}`}
        enterFrom="transform -translate-y-full"
        enterTo="transform translate-y-0"
        // Check whether the route change was triggered by the close menu button,
        // so that we only animate on button toggle, not on a route change.
        leave={
          isClosing
            ? `transition-transform ease-in duration-${MOBILE_NAVIGATION_ANIMATION_LENGTH}`
            : undefined
        }
        leaveFrom="transform translate-y-0"
        leaveTo="transform -translate-y-full"
        className="fixed z-20 flex h-[calc(100svh-56px)] w-full border-t bg-white xl:hidden"
      >
        <MobileNavigation
          items={items}
          isSignUp={isSignUp}
        />
      </Transition>
      <Cart
        isCartDrawerOpen={isCartDrawerOpen}
        setIsCartDrawerOpen={setIsCartDrawerOpen}
      >
        {children}
      </Cart>
    </>
  );
};
